import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Paper,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SCUILoadingLinear, SCUIDateRangerV2 } from '@/atomic/atoms';
import OtpPasswordQuery from '@/services/query/otpPassword/OtpPasswordQuery.services';
import TimeHelper from '@/utils/time';
import { format, subDays } from 'date-fns';
import { UPH } from './updateHistory.classes';
import { HourLimitCard } from './UpdateHistory.helpers';

export const UpdateHistory = ({ selectedCountry }: { selectedCountry: string }) => {
    const countries = JSON.parse(localStorage.getItem('countryData') || '{}')?.boSupportedCountries || [];

    const today = format(new Date(), 'yyyy-MM-dd');
    const sevenDaysAgo = format(subDays(new Date(), 7), 'yyyy-MM-dd');

    const [rateLimitHistory, setRateLimitHistory] = useState<any>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [expanded, setExpanded] = useState<string | false>(false);
    const [nestedExpanded, setNestedExpanded] = useState<string | false>(false);
    const [startDate, setStartDate] = useState<string>(sevenDaysAgo);
    const [endDate, setEndDate] = useState<string>(today);

    const { t } = useTranslation();

    const selectedCountryData = useMemo(
        () => countries.find((country: any) => country.countryId === selectedCountry),
        [countries, selectedCountry]
    );

    const getRateLimitHistory = () => {
        setIsLoading(true);
        OtpPasswordQuery.GetRateLimitHistory({
            countryId: selectedCountry,
            startDate,
            endDate,
        })
            .then((res: any) => {
                if (res?.data) {
                    const dataWithId = (res.data || []).map((item: any, index: number) => ({
                        ...item,
                        id: index,
                    }));
                    setRateLimitHistory(dataWithId);
                }
            })
            .catch(() => {
                Swal.fire(t('general.anErrorOccured'), '', 'error');
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        if (selectedCountry) {
            getRateLimitHistory();
        }
    }, [selectedCountry, startDate, endDate]);

    const handleDateChange = (event: any) => {
        setStartDate(event[0]);
        setEndDate(event[1]);
    };

    const handleAccordionChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleNestedAccordionChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
        setNestedExpanded(isExpanded ? panel : false);
    };

    return (
        <Box className={UPH.page} sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <div className={UPH.loadingSection}>
                <SCUILoadingLinear display={isLoading} />
            </div>
            <Box
                className={UPH.headerRow}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
            >
                <Typography className={UPH.title} sx={{ fontSize: '16px', fontWeight: 'bold' }}>
                    {t('otpPassword.updateHistory')}
                </Typography>
                <Box className={UPH.dateRangeWrap} sx={{ width: '300px' }}>
                    <div className={UPH.dateRanger}>
                        <SCUIDateRangerV2
                            fromDate={startDate}
                            toDate={endDate}
                            setDateRange={handleDateChange}
                            dateFormat="YYYY-MM-DD"
                            fullWidth
                        />
                    </div>
                </Box>
            </Box>
            <TableContainer className={UPH.tableContainer} sx={{ width: '100%', boxShadow: 3 }} component={Paper}>
                <Table className={UPH.table}>
                    <TableHead className={UPH.tableHead} sx={{ backgroundColor: '#e8e8e8' }}>
                        <TableRow className={UPH.tableHeadRow}>
                            <TableCell
                                className={UPH.tableHeadCellCountry}
                                sx={{ fontWeight: 'bold', fontSize: '14px', minWidth: '25%', width: '25%', p: 2 }}
                            >
                                {t('otpPassword.country')}
                            </TableCell>
                            <TableCell
                                className={UPH.tableHeadCellArchivedAt}
                                sx={{ fontWeight: 'bold', fontSize: '14px', minWidth: '25%', width: '25%', p: 2 }}
                            >
                                {t('otpPassword.archivedAt')}
                            </TableCell>
                            <TableCell
                                className={UPH.tableHeadCellUpdatedAt}
                                sx={{ fontWeight: 'bold', fontSize: '14px', minWidth: '25%', width: '25%', p: 2 }}
                            >
                                {t('otpPassword.updatedAt')}
                            </TableCell>
                            <TableCell
                                className={UPH.tableHeadCellUpdatedUserId}
                                sx={{ fontWeight: 'bold', fontSize: '14px', minWidth: '25%', width: '25%', p: 2 }}
                            >
                                {t('otpPassword.updatedUserId')}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                </Table>
                <Box className={UPH.accordionsWrap}>
                    {(rateLimitHistory || []).map((item: any, index: any) => (
                        <Accordion
                            className={UPH.accordion}
                            key={item.id}
                            expanded={expanded === `panel-${index}`}
                            onChange={handleAccordionChange(`panel-${index}`)}
                            disableGutters
                            elevation={0}
                            sx={{
                                '&:before': {
                                    display: 'none',
                                },
                                borderTop: index === 0 ? 'none' : '1px solid rgba(224, 224, 224, 1)',
                                '&:not(:last-child)': {
                                    borderBottom: 0,
                                },
                                '&.Mui-expanded': {
                                    margin: 0,
                                    backgroundColor: '#f0f0f0',
                                },
                            }}
                        >
                            <AccordionSummary
                                className={UPH.accordionSummary}
                                expandIcon={<ExpandMoreIcon className={UPH.accordionExpandIcon} />}
                                aria-controls={`panel-${index}-content`}
                                id={`panel-${index}`}
                                sx={{
                                    minHeight: '48px',
                                    '&.Mui-expanded': {
                                        minHeight: '48px',
                                    },
                                    '& .MuiAccordionSummary-content': {
                                        margin: 0,
                                        '&.Mui-expanded': {
                                            margin: 0,
                                        },
                                    },
                                    px: 2,
                                    py: 1.5,
                                }}
                            >
                                <Box
                                    className={UPH.accordionSummaryLayout}
                                    sx={{ display: 'flex', width: '100%', alignItems: 'center' }}
                                >
                                    <Box
                                        className={UPH.summaryCountryCol}
                                        sx={{ width: '25%', display: 'flex', alignItems: 'center' }}
                                    >
                                        {selectedCountryData?.flagUrl && (
                                            <img
                                                className={UPH.summaryCountryFlagImg}
                                                src={selectedCountryData?.flagUrl}
                                                alt={selectedCountryData?.countryCode}
                                                style={{
                                                    width: '30px',
                                                    height: '20px',
                                                    marginRight: '8px',
                                                    objectFit: 'contain',
                                                }}
                                            />
                                        )}
                                        <Typography className={UPH.summaryCountryText} sx={{ fontSize: '14px' }}>
                                            {selectedCountry} | {selectedCountryData?.countryName}
                                        </Typography>
                                    </Box>
                                    <Box className={UPH.summaryArchivedAtCol} sx={{ width: '25%' }}>
                                        <Typography
                                            className={UPH.summaryArchivedAtText}
                                            sx={{ fontSize: '14px', paddingLeft: '8px' }}
                                        >
                                            {item.archivedAt ? TimeHelper.formatDateHour(item.archivedAt) : '-'}
                                        </Typography>
                                    </Box>
                                    <Box className={UPH.summaryUpdatedAtCol} sx={{ width: '25%' }}>
                                        <Typography
                                            className={UPH.summaryUpdatedAtText}
                                            sx={{ fontSize: '14px', paddingLeft: '12px' }}
                                        >
                                            {item.updatedAt ? TimeHelper.formatDateHour(item.updatedAt) : '-'}
                                        </Typography>
                                    </Box>
                                    <Box className={UPH.summaryUpdatedUserIdCol} sx={{ width: '25%' }}>
                                        <Typography
                                            className={UPH.summaryUpdatedUserIdText}
                                            sx={{ fontSize: '14px', paddingLeft: '24px' }}
                                        >
                                            {item.updatedUserId || '-'}
                                        </Typography>
                                    </Box>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails
                                className={UPH.accordionDetails}
                                sx={{
                                    p: 2,
                                    border: '1px solid #e0e0e0',
                                    borderTop: '1px solid rgba(224, 224, 224, 1)',
                                    backgroundColor: '#f7f7f7',
                                }}
                            >
                                <Box className={UPH.detailsInner}>
                                    {/* Nested Accordions for limits */}
                                    {(item.limits || []).map((limitItem: any, limitIndex: number) => (
                                        <Accordion
                                            className={UPH.nestedAccordion}
                                            key={limitIndex}
                                            expanded={nestedExpanded === `nested-${index}-${limitIndex}`}
                                            onChange={handleNestedAccordionChange(`nested-${index}-${limitIndex}`)}
                                            disableGutters
                                            elevation={0}
                                            sx={{
                                                '&:before': {
                                                    display: 'none',
                                                },
                                                backgroundColor: '#ffffff',
                                                borderTop:
                                                    limitIndex === 0 ? 'none' : '1px solid rgba(224, 224, 224, 1)',
                                                '&:not(:last-child)': {
                                                    borderBottom: 0,
                                                },
                                                '&.Mui-expanded': {
                                                    margin: 0,
                                                    backgroundColor: '#f5f5f5',
                                                },
                                                mb: limitIndex < item.limits.length - 1 ? 1 : 0,
                                            }}
                                        >
                                            <AccordionSummary
                                                className={UPH.nestedAccordionSummary}
                                                expandIcon={<ExpandMoreIcon className={UPH.nestedAccordionExpandIcon} />}
                                                aria-controls={`nested-${index}-${limitIndex}-content`}
                                                id={`nested-${index}-${limitIndex}`}
                                                sx={{
                                                    minHeight: '40px',
                                                    '&.Mui-expanded': {
                                                        minHeight: '40px',
                                                    },
                                                    '& .MuiAccordionSummary-content': {
                                                        margin: 0,
                                                        '&.Mui-expanded': {
                                                            margin: 0,
                                                        },
                                                    },
                                                    px: 2,
                                                    py: 1,
                                                }}
                                            >
                                                <Box
                                                    className={UPH.nestedAccordionSummaryLayout}
                                                    sx={{
                                                        display: 'flex',
                                                        width: '100%',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                    }}
                                                >
                                                    <Box className={UPH.nestedEndpointCol} sx={{ flex: 1 }}>
                                                        <Typography
                                                            className={UPH.nestedEndpointLabel}
                                                            sx={{ fontSize: '14px', fontWeight: 600, color: '#333' }}
                                                        >
                                                            {t('otpPassword.endpoint')}:{' '}
                                                            <Box
                                                                className={UPH.nestedEndpointValue}
                                                                component="span"
                                                                sx={{ fontWeight: 400, ml: 0.5, fontSize: '12px' }}
                                                            >
                                                                {limitItem.endpoint || '-'}
                                                            </Box>
                                                        </Typography>
                                                    </Box>
                                                    <Box className={UPH.nestedPlatformCol} sx={{ flex: 1 }}>
                                                        <Typography
                                                            className={UPH.nestedPlatformLabel}
                                                            sx={{ fontSize: '14px', fontWeight: 600, color: '#333' }}
                                                        >
                                                            {t('otpPassword.platform')}:{' '}
                                                            <Box
                                                                className={UPH.nestedPlatformValue}
                                                                component="span"
                                                                sx={{ fontWeight: 400, ml: 0.5, fontSize: '12px' }}
                                                            >
                                                                {limitItem.platformName || '-'}
                                                            </Box>
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </AccordionSummary>
                                            <AccordionDetails
                                                className={UPH.nestedAccordionDetails}
                                                sx={{
                                                    p: 2,
                                                    borderTop: '1px solid rgba(224, 224, 224, 1)',
                                                    backgroundColor: '#fafafa',
                                                }}
                                            >
                                                {/* Grid layout for hour:limit values - 6 columns x 4 rows */}
                                                <Box
                                                    className={UPH.hoursGrid}
                                                    sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: 'repeat(6, 1fr)',
                                                        gap: '12px',
                                                        width: '100%',
                                                    }}
                                                >
                                                    {Array.from({ length: 24 }).map((_, hourIdx) => (
                                                        <HourLimitCard
                                                            key={hourIdx}
                                                            hourIdx={hourIdx}
                                                            values={limitItem.values}
                                                        />
                                                    ))}
                                                </Box>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                                </Box>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            </TableContainer>
        </Box>
    );
};
